import React, { useState } from "react";
import { observer } from "mobx-react";
import {
  AccommodationTravelStatus,
  BookingStatusType,
  CustomerType,
  SearchProfileType,
  TimeReportStatus,
  useBookingProfile_GetTagsQuery,
  useGetUserSelectUsersQuery
} from "@graphql";
import {
  DEMAND_SOURCE_FILTER_OPTIONS,
  EMPTY_DEMAND_SOURCE,
  demandSourceLabel
} from "@bonliva-crm/shared";
import { BookingFilterManager } from "./BookingFilterManager";
import SearchForm from "@search/SearchForm";
import Searchbar from "@search/Searchbar";
import {
  Grid3,
  renderAOE,
  renderConsultantSelector,
  renderCustomerSelector,
  renderCustomerTypeSelectHospitalOnly,
  renderFrom,
  renderHasAccommodationBenefitTax,
  renderHasBookingKits,
  renderLegalPersonType,
  renderMultiSelect,
  renderNationalProcurement,
  renderOrgTypeSelect,
  renderProjectSelector,
  renderRegions,
  renderSelect,
  renderTextField,
  renderTo,
  renderUsers
} from "@search/filters";
import { getOptions } from "@components/CostCenterSelect";
import {
  hasBonlivaCareCostCenter,
  TimeReportStatus as Statuses
} from "@bonliva-crm/shared";
import { BookingKitSupportFilterManager } from "./BookingKitSupportFilterManager";
import AddSearchProfileDialog from "@containers/VirtualSelector/SearchProfileSelector/AddSearchProfileDialog/AddSearchProfileDialog";
import { SearchProfileSelectorStore } from "@containers/VirtualSelector/SearchProfileSelector/SearchProfileSelectorStore";
import { Button, Divider, Grid } from "@material-ui/core";
import { SearchProfileSelector } from "@containers/VirtualSelector/SearchProfileSelector/SearchProfileSelector";
import { RegularButton } from "@common";
import { Close } from "@material-ui/icons";
import { BookingFilterGroup, Filter, optAllYesNo } from "./utils";
import { useLocale } from "@hooks";

type Props = {
  manager: BookingFilterManager | BookingKitSupportFilterManager;
  group: BookingFilterGroup;
};

export const BookingSearchForm = observer((props: Props) => {
  const { manager, group } = props;
  const { locale } = useLocale();
  const [searchProfileStore] = useState(
    () => new SearchProfileSelectorStore(manager, SearchProfileType.Bookings)
  );

  const { data: tagsData } = useBookingProfile_GetTagsQuery();
  const { data: usersData } = useGetUserSelectUsersQuery();

  const users = usersData?.users ?? [];
  const isKitSupport = group === BookingFilterGroup.KitSupport;
  const isSyncTab = group === BookingFilterGroup.Sync;
  const isFinanceTab = group === BookingFilterGroup.Finance;
  const isAuditTab = group === BookingFilterGroup.Audit;

  const searchbar = (
    <Searchbar
      onChange={(v) => manager.updateFilter({ text: v }, true)}
      value={manager.filter.text}
      disabled={manager.isFilterDisabled("text")}
    />
  );

  const aoe = manager.renderField("areasOfExpertise", () => {
    return renderAOE(
      (v) => manager.updateFilter({ areasOfExpertise: v }),
      manager.filter.areasOfExpertise,
      manager.isFilterDirty("areasOfExpertise"),
      manager.isFilterDisabled("areasOfExpertise"),
      false
    );
  });

  const consultantSelector = manager.renderField("customerNumber", () =>
    renderConsultantSelector(
      (v) => {
        return manager.updateFilter({
          consultants: v.toLabelValue(
            (x) => x.Name,
            (x) => x._id
          )
        });
      },
      manager.filter.consultants,
      manager.isFilterDirty("consultants"),
      { disabled: manager.isFilterDisabled("consultants") }
    )
  );

  const customerSelector = manager.renderField("customerNumber", () =>
    renderCustomerSelector(
      (v) =>
        manager.updateFilter({
          customers: v.toLabelValue(
            (x) => x.Name,
            (x) => x._id
          )
        }),
      manager.filter.customers,
      manager.isFilterDirty("customers"),
      { disabled: manager.isFilterDisabled("customers"), hideButton: true },
      { customerType: [CustomerType.Hospital, CustomerType.Department] }
    )
  );

  const ba = manager.renderField("responsiblePersonId", () =>
    renderUsers(
      users,
      locale.SelectSomething(locale["Responsible BA"]),
      manager.filter.responsiblePersonId,
      (v) => manager.updateFilter({ responsiblePersonId: v }),
      manager.isFilterDirty("responsiblePersonId"),
      manager.isFilterDisabled("responsiblePersonId")
    )
  );

  const allProjectsFor = manager.renderField("projectResponsiblePersonId", () =>
    renderUsers(
      users,
      locale.SelectSomething(locale.AllProjectsFor),
      manager.filter.projectResponsiblePersonId,
      (v) => manager.updateFilter({ projectResponsiblePersonId: v }),
      manager.isFilterDirty("projectResponsiblePersonId"),
      manager.isFilterDisabled("projectResponsiblePersonId")
    )
  );

  const assignedPersons = !isSyncTab
    ? manager.renderField("assignedPersons", () =>
        renderUsers(
          users,
          locale.SelectSomething(locale.managedBy),
          manager.filter.assignedPersons,
          (v) => manager.updateFilter({ assignedPersons: v }),
          manager.isFilterDirty("assignedPersons"),
          manager.isFilterDisabled("assignedPersons"),
          isKitSupport
        )
      )
    : null;

  const from = manager.renderField("from", () =>
    renderFrom(
      manager.filter.from,
      (v) => manager.updateFilter({ from: v }),
      manager.isFilterDirty("from"),
      manager.filter.to
    )
  );

  const to = manager.renderField("to", () =>
    renderTo(
      manager.filter.to,
      (v) => manager.updateFilter({ to: v }),
      manager.isFilterDirty("to"),
      manager.filter.from
    )
  );

  const customerNumber = manager.renderField("customerNumber", () =>
    renderTextField(
      locale.CustomerNumberShort,
      manager.filter.customerNumber,
      (v) => manager.updateFilter({ customerNumber: v }, true),
      manager.isFilterDirty("customerNumber"),
      manager.isFilterDisabled("customerNumber")
    )
  );

  const callNumber = manager.renderField("callNumber", () =>
    renderTextField(
      locale.CallNumber,
      manager.filter.callNumber,
      (v) => manager.updateFilter({ callNumber: v }, true),
      manager.isFilterDirty("callNumber"),
      manager.isFilterDisabled("callNumber")
    )
  );

  const projectId = manager.renderField("projectId", () =>
    renderProjectSelector(
      manager.filter.projectId,
      (v) => manager.updateFilter({ projectId: v }),
      manager.isFilterDirty("projectId"),
      manager.isFilterDisabled("projectId")
    )
  );

  const invoiceNumber = !isSyncTab
    ? manager.renderField("invoiceNumber", () =>
        renderTextField(
          locale.InvoiceNumber,
          manager.filter.invoiceNumber,
          (v) => manager.updateFilter({ invoiceNumber: v }, true),
          manager.isFilterDirty("invoiceNumber"),
          manager.isFilterDisabled("invoiceNumber")
        )
      )
    : null;

  const bookingStatus = !isSyncTab
    ? manager.renderField("bookingStatus", () => {
        return renderMultiSelect(
          locale.BookingStatus,
          Object.values(BookingStatusType).toLabelValue((x) => locale.bookingStatus[x]),
          false,
          manager.filter.bookingStatus,
          (v: BookingStatusType[]) => manager.updateFilter({ bookingStatus: v }),
          manager.isFilterDisabled("bookingStatus"),
          manager.isFilterDirty("bookingStatus")
        );
      })
    : null;

  const bookingSupportTagStatus = !isSyncTab
    ? manager.renderField("bookingSupportTag", () => {
        return renderMultiSelect(
          locale.BookingTagStatus,
          tagsData?.config.bookingTags
            .filter((x) => x.booking)
            .toLabelValue(
              (x) => x.label,
              (x) => x.code
            ) || [],
          false,
          manager.filter.bookingSupportTag,
          (v) => manager.updateFilter({ bookingSupportTag: v }),
          manager.isFilterDisabled("bookingSupportTag"),
          manager.isFilterDirty("bookingSupportTag")
        );
      })
    : null;

  // Where the demand came from (ADR-0008, TR-4). Not the same question as how
  // the hours were reported — that is the row source column — so it sits beside
  // the other week-level filters rather than with the time report ones.
  const demandSource = !isSyncTab
    ? manager.renderField("demandSource", () => {
        return renderMultiSelect(
          locale.DemandSource,
          [...DEMAND_SOURCE_FILTER_OPTIONS].toLabelValue(
            (x) => (x === EMPTY_DEMAND_SOURCE ? locale.DemandSourceEmpty : demandSourceLabel(x)),
            (x) => x
          ),
          false,
          manager.filter.demandSource,
          (v: string[]) => manager.updateFilter({ demandSource: v }),
          manager.isFilterDisabled("demandSource"),
          manager.isFilterDirty("demandSource")
        );
      })
    : null;

  const unionStatusList: any[] = [
    Statuses.Blank,
    Statuses.Draft,
    Statuses.SubmittedByConsultant,
    Statuses.SentToConsultant,
    Statuses.SentToCustomer,
    Statuses.ApprovedByCustomer,
    Statuses.RejectedByCustomer,
    Statuses.ConsultantPaymentProcessed
  ];

  const timeReportStatus = !isSyncTab
    ? manager.renderField("timeReportStatus", () =>
        renderMultiSelect(
          locale.TimeReported,
          unionStatusList.toLabelValue((t) => locale.timeReportStatus[t] ?? ""),
          false,
          manager.filter.timeReportStatus,
          (v: TimeReportStatus[]) => manager.updateFilter({ timeReportStatus: v }),
          manager.isFilterDisabled("timeReportStatus"),
          manager.isFilterDirty("timeReportStatus")
        )
      )
    : null;

  const timeReportStatusSetBy = manager.renderField("timeReportStatusSetBy", () =>
    renderUsers(
      users,
      locale.SelectSomething(locale.ByUser),
      manager.filter.timeReportStatusSetBy,
      (v) => manager.updateFilter({ timeReportStatusSetBy: v }),
      manager.isFilterDirty("timeReportStatusSetBy"),
      manager.isFilterDisabled("timeReportStatusSetBy")
    )
  );

  const costCenter = manager.renderField("costCenter", () => {
    return renderMultiSelect(
      locale.CostCenter,
      getOptions(manager.costCenters, true),
      true,
      manager.filter.costCenter,
      (v) => {
        const filter: Partial<Filter> = { costCenter: v };
        if (!hasBonlivaCareCostCenter(v)) {
          filter.patientId = "";
        }

        manager.updateFilter(filter);
      },
      manager.isFilterDisabled("costCenter"),
      manager.isFilterDirty("costCenter")
    );
  });

  const patientId = manager.renderField("costCenter", () => {
    return (
      hasBonlivaCareCostCenter(manager.filter.costCenter) &&
      renderTextField(
        locale.PatientId,
        manager.filter.patientId,
        (v) => manager.updateFilter({ patientId: v }, true),
        manager.isFilterDirty("patientId"),
        manager.isFilterDisabled("patientId")
      )
    );
  });

  const customerType = manager.renderField("customerGroup", () =>
    renderCustomerTypeSelectHospitalOnly(
      manager.filter.customerGroup,
      (v) => manager.updateFilter({ customerGroup: v }),
      manager.isFilterDisabled("customerGroup"),
      manager.isFilterDirty("customerGroup")
    )
  );

  const orgType = manager.renderField("organizationType", () =>
    renderOrgTypeSelect(
      manager.filter.organizationType,
      (v) => manager.updateFilter({ organizationType: v }),
      manager.isFilterDisabled("organizationType"),
      manager.isFilterDirty("organizationType")
    )
  );

  const region = manager.renderField("region", () =>
    renderRegions(
      manager.filter.region,
      (v) => manager.updateFilter({ region: v }),
      manager.isFilterDirty("region")
    )
  );

  const accommodationStatus =
    !isSyncTab && !isKitSupport
      ? manager.renderField("accommodationStatus", () =>
          renderMultiSelect(
            locale.Accommodation,
            Object.values(AccommodationTravelStatus).toLabelValue(
              (x) => locale.accommodationTravelStatus[x]
            ),
            false,
            manager.filter.accommodationStatus,
            (v: AccommodationTravelStatus[]) =>
              manager.updateFilter({ accommodationStatus: v }),
            manager.isFilterDisabled("accommodationStatus"),
            manager.isFilterDirty("accommodationStatus")
          )
        )
      : null;

  const travelStatus =
    !isSyncTab && !isKitSupport
      ? manager.renderField("travelStatus", () =>
          renderMultiSelect(
            locale.Travel,
            Object.values(AccommodationTravelStatus).toLabelValue(
              (x) => locale.accommodationTravelStatus[x]
            ),
            false,
            manager.filter.travelStatus,
            (v: AccommodationTravelStatus[]) => manager.updateFilter({ travelStatus: v }),
            manager.isFilterDisabled("travelStatus"),
            manager.isFilterDirty("travelStatus")
          )
        )
      : null;

  const legalPersonType = manager.renderField("legalPersonType", () =>
    renderLegalPersonType(
      manager.filter.legalPersonType,
      (v) => manager.updateFilter({ legalPersonType: v }),
      manager.isFilterDirty("legalPersonType")
    )
  );

  const nationalProcurement =
    isFinanceTab || isAuditTab || isKitSupport
      ? manager.renderField("nationalProcurement", () =>
          renderNationalProcurement(
            manager.filter.nationalProcurement,
            (v) => manager.updateFilter({ nationalProcurement: v }),
            manager.isFilterDirty("nationalProcurement")
          )
        )
      : null;

  const groupInvoicing = isKitSupport
    ? manager.renderField("groupInvoicing", () =>
        renderSelect(
          locale.bookingSupportChecks.groupInvoicing,
          optAllYesNo,
          false,
          manager.filter.groupInvoicing,
          (v) => manager.updateFilter({ groupInvoicing: v }),
          manager.isFilterDisabled("groupInvoicing"),
          manager.isFilterDirty("groupInvoicing")
        )
      )
    : null;

  const showTRStatusSetBy = manager.filter.timeReportStatus.some(
    (x) =>
      x === TimeReportStatus.SentToCustomer || x === TimeReportStatus.ApprovedByCustomer
  );

  const hasBookingKits = manager.renderField("hasBookingKits", () =>
    renderHasBookingKits(
      manager.filter.hasBookingKits,
      (v) => manager.updateFilter({ hasBookingKits: v }),
      manager.isFilterDirty("hasBookingKits")
    )
  );

  const hasAccommodationBenefitTax = manager.renderField(
    "hasAccommodationBenefitTax",
    () =>
      renderHasAccommodationBenefitTax(
        manager.filter.hasAccommodationBenefitTax,
        (v) => manager.updateFilter({ hasAccommodationBenefitTax: v }),
        manager.isFilterDirty("hasAccommodationBenefitTax")
      )
  );

  const addSearchProfile = (
    <Grid xs={12} item style={{ marginTop: 10 }}>
      <Grid container justifyContent="flex-end">
        <Button onClick={searchProfileStore.toggleForm}>
          {locale.SaveSearchProfile}
        </Button>
        {searchProfileStore.selectedProfile && (
          <>
            <Button
              onClick={() =>
                searchProfileStore.update(searchProfileStore.selectedProfile._id)
              }
            >
              {locale.UpdateSearchProfile}
            </Button>
            <Button
              onClick={() =>
                searchProfileStore.remove(searchProfileStore.selectedProfile._id)
              }
            >
              {locale.deleteProfile}
            </Button>
          </>
        )}
        <Divider orientation="vertical" flexItem variant="middle" />
        <RegularButton
          rightIcon={<Close />}
          size="small"
          variant="text"
          onClick={() => {
            manager.clearFilter();
          }}
        >
          {locale.Clear}
        </RegularButton>
      </Grid>
    </Grid>
  );

  const searchProfile = (
    <Grid3 dirty={false}>
      <SearchProfileSelector
        type={SearchProfileType.Bookings}
        onRemove={searchProfileStore.remove}
        value={searchProfileStore.selectedProfile}
        onChange={searchProfileStore.change}
      />
    </Grid3>
  );

  return (
    <SearchForm searchbar={searchbar}>
      {allProjectsFor} {ba} {customerType} {aoe}
      {orgType} {consultantSelector} {customerSelector} {customerNumber}
      {from} {to} {projectId} {invoiceNumber}
      {bookingStatus} {timeReportStatus} {showTRStatusSetBy && timeReportStatusSetBy}{" "}
      {costCenter} {legalPersonType} {region}
      {travelStatus} {accommodationStatus} {assignedPersons} {bookingSupportTagStatus}
      {patientId} {demandSource} {searchProfile}
      {groupInvoicing} {nationalProcurement} {callNumber} {hasBookingKits}{" "}
      {hasAccommodationBenefitTax} {addSearchProfile}
      <AddSearchProfileDialog
        hideCustomer
        getInput={searchProfileStore.getInput}
        open={searchProfileStore.isOpenDialog}
        onClose={searchProfileStore.toggleForm}
      />
    </SearchForm>
  );
});
